import * as XLSX from 'xlsx-js-style';
import { KioskSessionGridRow, HierarchicalSessionRow, LocationGroupRow } from '@/features/kiosk-sessions/types/kiosk-session-types';

interface ExcelRow {
  'Session ID'?: string;
  'Status'?: string;
  'EVSE ID'?: string;
  'Connector ID'?: string;
  'CDR'?: string;
  'Start Time'?: string;
  'End Time'?: string;
  'Duration'?: string;
  'Total Energy (kWh)'?: number;
  // NEW TRANSACTION FIELDS (HIVE-6397)
  'Auth Amount'?: number;
  'Settlement Amount'?: number;
  'Total inc VAT'?: number;
  'Total exc VAT'?: number;
  'VAT Amount'?: number;
  'Payment Method'?: string;
  'Card Identifier'?: string;
  'Connector Alias'?: string;
  'Machine Name'?: string;
  'Transaction ID'?: string;
  // Additional fields
  'Auth Method'?: string;
  'Connector Type'?: string;
  'Max Power (kW)'?: number;
  'Power Type'?: string;
  'Receipt'?: string;
  'Location'?: string;
  // PS-158
  'Phone Number'?: string;
  'License Plate'?: string;
}

// PS-158: phone and plate are always part of the export, even when hidden in the grid
const ALWAYS_EXPORTED_COLUMNS: (keyof ColumnVisibility)[] = ['phone_number', 'plate'];

interface ColumnVisibility {
  session_id?: boolean;
  status?: boolean;
  evse_id?: boolean;
  connector_id?: boolean;
  cdr_id?: boolean;
  start_time?: boolean;
  stop_time?: boolean;
  duration?: boolean;
  total_energy?: boolean;
  // NEW TRANSACTION FIELDS (HIVE-6397)
  auth_amount?: boolean;
  settlement_amount?: boolean;
  total_inc_vat?: boolean;
  total_exc_vat?: boolean;
  vat_amount?: boolean;
  payment_method?: boolean;
  card_identifier?: boolean;
  connector_alias?: boolean;
  machine_name?: boolean;
  payment_transaction_id?: boolean;
  // Additional fields
  auth_method?: boolean;
  connector_standard?: boolean;
  max_power?: boolean;
  power_type?: boolean;
  receipt?: boolean;
  location_name?: boolean;
  // PS-158
  phone_number?: boolean;
  plate?: boolean;
}

/**
 * Calculate optimal column width based on content
 */
const calculateColumnWidth = (data: ExcelRow[], key: keyof ExcelRow, headerLength: number): number => {
  const maxLength = data.reduce((max, row) => {
    const cellLength = row[key]?.toString().length || 0;
    return Math.max(max, cellLength);
  }, headerLength);
  
  // Add some padding and cap at reasonable maximum
  return Math.min(Math.max(maxLength + 2, 10), 60);
};

/**
 * Export kiosk session data to Excel format
 * Only exports columns that are visible in the grid (phone and plate are always exported)
 */
export const exportKioskSessionsToExcel = (
  data: KioskSessionGridRow[],
  columnVisibility: ColumnVisibility = {},
  filename?: string
) => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Map column IDs to Excel headers - dynamically supports all grid columns
  const columnMapping: { id: keyof ColumnVisibility; header: keyof ExcelRow; getValue: (session: KioskSessionGridRow) => string | number }[] = [
    { id: 'session_id', header: 'Session ID', getValue: (s) => s.session_id },
    { id: 'status', header: 'Status', getValue: (s) => s.status || 'UNKNOWN' },
    { id: 'evse_id', header: 'EVSE ID', getValue: (s) => s.evse_id || '' },
    { id: 'connector_id', header: 'Connector ID', getValue: (s) => s.connector_id || '' },
    { id: 'cdr_id', header: 'CDR', getValue: (s) => s.cdr_id || '' },
    { id: 'start_time', header: 'Start Time', getValue: (s) => formatDateForExcel(s.start_time) },
    { id: 'stop_time', header: 'End Time', getValue: (s) => formatDateForExcel(s.stop_time) },
    { id: 'duration', header: 'Duration', getValue: (s) => formatDurationForExcel(s.start_time, s.stop_time) },
    { id: 'total_energy', header: 'Total Energy (kWh)', getValue: (s) => s.total_energy || 0 },
    // NEW TRANSACTION FIELDS (HIVE-6397)
    { id: 'auth_amount', header: 'Auth Amount', getValue: (s) => s.authAmount || 0 },
    { id: 'settlement_amount', header: 'Settlement Amount', getValue: (s) => s.settAmount || 0 },
    { id: 'total_inc_vat', header: 'Total inc VAT', getValue: (s) => s.totalIncVat || 0 },
    { id: 'total_exc_vat', header: 'Total exc VAT', getValue: (s) => s.totalExcVat || 0 },
    { id: 'vat_amount', header: 'VAT Amount', getValue: (s) => s.vatAmount || 0 },
    { id: 'payment_method', header: 'Payment Method', getValue: (s) => formatPaymentMethodForExcel(s.paymentMethod) },
    { id: 'card_identifier', header: 'Card Identifier', getValue: (s) => s.cardIdentifier ? `****${s.cardIdentifier}` : '' },
    { id: 'connector_alias', header: 'Connector Alias', getValue: (s) => s.connector_alias || '' },
    { id: 'machine_name', header: 'Machine Name', getValue: (s) => s.machine_name || '' },
    { id: 'payment_transaction_id', header: 'Transaction ID', getValue: (s) => s.payment_transaction_id || '' },
    // Additional fields
    { id: 'auth_method', header: 'Auth Method', getValue: (s) => formatAuthMethodForExcel(s.auth_method) },
    { id: 'connector_standard', header: 'Connector Type', getValue: (s) => formatConnectorStandardForExcel(s.connector_standard) },
    { id: 'max_power', header: 'Max Power (kW)', getValue: (s) => s.max_power || 0 },
    { id: 'power_type', header: 'Power Type', getValue: (s) => formatPowerTypeForExcel(s.power_type) },
    { id: 'receipt', header: 'Receipt', getValue: (s) => s.receipt || '' },
    { id: 'location_name', header: 'Location', getValue: (s) => s.location_name || '' },
    // PS-158: empty cell when not collected
    { id: 'phone_number', header: 'Phone Number', getValue: (s) => s.phone_number || '' },
    { id: 'plate', header: 'License Plate', getValue: (s) => s.plate || '' }
  ];

  // Filter visible columns
  const visibleColumns = columnMapping.filter(col => ALWAYS_EXPORTED_COLUMNS.includes(col.id) || columnVisibility[col.id] !== false);

  // Prepare data for Excel export with only visible columns
  const excelData: ExcelRow[] = data.map(session => {
    const row: ExcelRow = {};
    visibleColumns.forEach(col => {
      (row as any)[col.header] = col.getValue(session);
    });
    return row;
  });

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Auto-resize columns based on content (only visible columns)
  const headers = visibleColumns.map(col => col.header);

  const columnWidths = headers.map(header => ({
    wch: calculateColumnWidth(excelData, header, header.length)
  }));
  
  worksheet['!cols'] = columnWidths;

  // Make header row bold with styling
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cell = worksheet[cellAddress];
    if (!cell) continue;
    
    // Apply bold font and center alignment to header cells
    cell.s = {
      font: { 
        bold: true,
        sz: 11,
        color: { rgb: "000000" }
      },
      alignment: { 
        horizontal: 'left', 
        vertical: 'center',
        wrapText: false
      },
      fill: {
        fgColor: { rgb: "E7E6E6" }
      },
      border: {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      }
    };
  }

  // Add auto-filter to header row
  worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${excelData.length + 1}` };

  // Add the worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Kiosk Sessions');

  // Generate filename with current date if not provided
  const exportFilename = filename || `kiosk-sessions-${formatDateForFilename(new Date())}.xlsx`;

  // Write and download the file
  XLSX.writeFile(workbook, exportFilename);
};

/**
 * Format date for Excel display (DD/MM/YYYY HH:mm)
 */
const formatDateForExcel = (date: Date): string => {
  if (!date || !(date instanceof Date)) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Format duration between two dates for Excel
 */
const formatDurationForExcel = (startTime?: Date, stopTime?: Date): string => {
  if (!startTime || !stopTime || !(startTime instanceof Date) || !(stopTime instanceof Date) ||
      isNaN(startTime.getTime()) || isNaN(stopTime.getTime())) {
    return '';
  }

  const durationMs = stopTime.getTime() - startTime.getTime();

  if (durationMs < 0) {
    return '';
  }

  const hours = Math.floor(durationMs / (1000 * 60 * 60));
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours === 0 && minutes === 0) {
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    return `${seconds}s`;
  }

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

/**
 * Format payment method for Excel
 */
const formatPaymentMethodForExcel = (method?: string): string => {
  if (!method) return '';
  switch (method.toUpperCase()) {
    case 'CC': return 'Credit Card';
    case 'PP': return 'PayPal';
    default: return method;
  }
};

/**
 * Format auth method for Excel
 */
const formatAuthMethodForExcel = (method?: string): string => {
  if (!method || method === 'UNKNOWN') return '';
  const methodMap: Record<string, string> = {
    'AUTH_REQUEST': 'Auth Request',
    'COMMAND': 'Command',
    'WHITELIST': 'Whitelist'
  };
  return methodMap[method] || method;
};

/**
 * Format connector standard for Excel
 */
const formatConnectorStandardForExcel = (standard?: string): string => {
  if (!standard || standard === 'Unknown') return '';
  return standard
    .replace('IEC_62196_', '')
    .replace('_COMBO', ' Combo')
    .replace(/_/g, ' ');
};

/**
 * Format power type for Excel
 */
const formatPowerTypeForExcel = (powerType?: string): string => {
  if (!powerType || powerType === 'UNKNOWN') return '';
  const powerMap: Record<string, string> = {
    'AC_1_PHASE': 'AC 1-Phase',
    'AC_2_PHASE': 'AC 2-Phase',
    'AC_2_PHASE_SPLIT': 'AC Split',
    'AC_3_PHASE': 'AC 3-Phase',
    'DC': 'DC'
  };
  return powerMap[powerType] || powerType;
};

/**
 * Format date for filename (YYYY-MM-DD)
 */
const formatDateForFilename = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Export kiosk sessions to Excel in grouped format (by location)
 * Creates a hierarchical layout with location headers and subtotals
 */
export const exportKioskSessionsToExcelGrouped = (
  data: HierarchicalSessionRow[],
  columnVisibility: ColumnVisibility = {},
  filename?: string
) => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Map column IDs to Excel headers - same as flat export
  const columnMapping: { id: keyof ColumnVisibility; header: keyof ExcelRow; getValue: (session: KioskSessionGridRow) => string | number }[] = [
    { id: 'session_id', header: 'Session ID', getValue: (s) => s.session_id },
    { id: 'status', header: 'Status', getValue: (s) => s.status || 'UNKNOWN' },
    { id: 'evse_id', header: 'EVSE ID', getValue: (s) => s.evse_id || '' },
    { id: 'connector_id', header: 'Connector ID', getValue: (s) => s.connector_id || '' },
    { id: 'cdr_id', header: 'CDR', getValue: (s) => s.cdr_id || '' },
    { id: 'start_time', header: 'Start Time', getValue: (s) => formatDateForExcel(s.start_time) },
    { id: 'stop_time', header: 'End Time', getValue: (s) => formatDateForExcel(s.stop_time) },
    { id: 'duration', header: 'Duration', getValue: (s) => formatDurationForExcel(s.start_time, s.stop_time) },
    { id: 'total_energy', header: 'Total Energy (kWh)', getValue: (s) => s.total_energy || 0 },
    // NEW TRANSACTION FIELDS (HIVE-6397)
    { id: 'auth_amount', header: 'Auth Amount', getValue: (s) => s.authAmount || 0 },
    { id: 'settlement_amount', header: 'Settlement Amount', getValue: (s) => s.settAmount || 0 },
    { id: 'total_inc_vat', header: 'Total inc VAT', getValue: (s) => s.totalIncVat || 0 },
    { id: 'total_exc_vat', header: 'Total exc VAT', getValue: (s) => s.totalExcVat || 0 },
    { id: 'vat_amount', header: 'VAT Amount', getValue: (s) => s.vatAmount || 0 },
    { id: 'payment_method', header: 'Payment Method', getValue: (s) => formatPaymentMethodForExcel(s.paymentMethod) },
    { id: 'card_identifier', header: 'Card Identifier', getValue: (s) => s.cardIdentifier ? `****${s.cardIdentifier}` : '' },
    { id: 'connector_alias', header: 'Connector Alias', getValue: (s) => s.connector_alias || '' },
    { id: 'machine_name', header: 'Machine Name', getValue: (s) => s.machine_name || '' },
    { id: 'payment_transaction_id', header: 'Transaction ID', getValue: (s) => s.payment_transaction_id || '' },
    // Additional fields
    { id: 'auth_method', header: 'Auth Method', getValue: (s) => formatAuthMethodForExcel(s.auth_method) },
    { id: 'connector_standard', header: 'Connector Type', getValue: (s) => formatConnectorStandardForExcel(s.connector_standard) },
    { id: 'max_power', header: 'Max Power (kW)', getValue: (s) => s.max_power || 0 },
    { id: 'power_type', header: 'Power Type', getValue: (s) => formatPowerTypeForExcel(s.power_type) },
    { id: 'receipt', header: 'Receipt', getValue: (s) => s.receipt || '' },
    { id: 'location_name', header: 'Location', getValue: (s) => s.location_name || '' },
    // PS-158: empty cell when not collected
    { id: 'phone_number', header: 'Phone Number', getValue: (s) => s.phone_number || '' },
    { id: 'plate', header: 'License Plate', getValue: (s) => s.plate || '' }
  ];

  // Filter visible columns
  const visibleColumns = columnMapping.filter(col => ALWAYS_EXPORTED_COLUMNS.includes(col.id) || columnVisibility[col.id] !== false);
  const headers = visibleColumns.map(col => col.header);

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheetData: any[] = [];

  // Add header row
  const headerRow: any = {};
  headers.forEach(header => {
    headerRow[header] = header;
  });
  worksheetData.push(headerRow);

  // Track grand totals
  let grandTotalSessions = 0;
  let grandTotalEnergy = 0;
  let grandTotalRevenue = 0;

  // Process each location group
  data.forEach((row: any) => {
    if (row.type === 'location') {
      const locationRow = row as LocationGroupRow;
      
      // Add location header row
      const locationHeaderRow: any = {};
      const locationHeader = `${locationRow.locationName} - ${locationRow.sessionCount} sessions | ${locationRow.totalEnergy.toFixed(2)} kWh | ₪${locationRow.totalRevenue.toFixed(2)}`;
      locationHeaderRow[headers[0]] = locationHeader;
      worksheetData.push(locationHeaderRow);

      // Update grand totals
      grandTotalSessions += locationRow.sessionCount;
      grandTotalEnergy += locationRow.totalEnergy;
      grandTotalRevenue += locationRow.totalRevenue;

      // Add session rows for this location
      locationRow.subRows.forEach(session => {
        const sessionRow: any = {};
        visibleColumns.forEach(col => {
          sessionRow[col.header] = col.getValue(session);
        });
        worksheetData.push(sessionRow);
      });

      // Add empty row after each location group for spacing
      worksheetData.push({});
    }
  });

  // Add grand totals row
  const grandTotalRow: any = {};
  grandTotalRow[headers[0]] = `GRAND TOTALS: ${grandTotalSessions} sessions | ${grandTotalEnergy.toFixed(2)} kWh | ₪${grandTotalRevenue.toFixed(2)}`;
  worksheetData.push(grandTotalRow);

  // Convert to worksheet
  const worksheet = XLSX.utils.json_to_sheet(worksheetData, { skipHeader: true });

  // Get worksheet range
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

  // Style the header row (row 0)
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cell = worksheet[cellAddress];
    if (!cell) continue;
    
    cell.s = {
      font: { bold: true, sz: 11, color: { rgb: "000000" } },
      alignment: { horizontal: 'left', vertical: 'center', wrapText: false },
      fill: { fgColor: { rgb: "E7E6E6" } },
      border: {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      }
    };
  }

  // Style location header rows and grand total row
  let currentRow = 1; // Start after header row
  data.forEach((row: any) => {
    if (row.type === 'location') {
      const locationRow = row as LocationGroupRow;
      
      // Style location header row
      const cellAddress = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
      const cell = worksheet[cellAddress];
      if (cell) {
        cell.s = {
          font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: 'left', vertical: 'center' },
          fill: { fgColor: { rgb: "4472C4" } }
        };
      }
      
      currentRow++; // Move past location header
      currentRow += locationRow.subRows.length; // Move past all session rows
      currentRow++; // Move past empty spacing row
    }
  });

  // Style grand total row
  const grandTotalRowIndex = range.e.r;
  const grandTotalCellAddress = XLSX.utils.encode_cell({ r: grandTotalRowIndex, c: 0 });
  const grandTotalCell = worksheet[grandTotalCellAddress];
  if (grandTotalCell) {
    grandTotalCell.s = {
      font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { fgColor: { rgb: "70AD47" } }
    };
  }

  // Set column widths
  const columnWidths = headers.map((header, idx) => {
    if (idx === 0) {
      return { wch: 50 }; // Wider first column for location names
    }
    return { wch: 15 };
  });
  worksheet['!cols'] = columnWidths;

  // Add the worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Kiosk Sessions Grouped');

  // Generate filename with current date if not provided
  const exportFilename = filename || `kiosk-sessions-grouped-${formatDateForFilename(new Date())}.xlsx`;

  // Write and download the file
  XLSX.writeFile(workbook, exportFilename);
};